import useSWR from 'swr';
import { useMemo } from 'react';
// utils
import axios, { fetcher, endpoints } from 'src/utils/axios';

// ----------------------------------------------------------------------

export function useGetPendingPatients() {
  const URL = endpoints.patients.pending;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);

  const memoizedValue = useMemo(
    () => ({
      pendingPatients: data?.items || [],
      pendingPatientsLoading: isLoading,
      pendingPatientsError: error,
      pendingPatientsValidating: isValidating,
      pendingPatientsEmpty: !isLoading && !data?.items?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

// ----------------------------------------------------------------------

export function useGetMyApprovals(userPermissions = []) {
  const URL = endpoints.patients.myApprovals;

  const { data, isLoading, error, isValidating, mutate } = useSWR(URL, fetcher);

  const getNormalizedStatus = (item) =>
    String(item?.approvalStatus || item?.status || '')
      .trim()
      .toLowerCase();

  const toArray = (value) => (Array.isArray(value) ? value : []);

  const toStageList = (value) => (Array.isArray(value) ? value : [value]);

  const ROLE_STAGE_MAP = [
    { permission: 'admin', stages: ['admin-review'] },
    { permission: 'doctor', stages: ['doctor-review'] },
    { permission: 'manager', stages: ['manager-approval'] },
    { permission: 'cco', stages: ['cco-approval'] },
    { permission: 'ceo', stages: ['final-approval'] },
    { permission: 'executive_director', stages: ['final-approval'] },
  ];

  const getViewerStages = () =>
    ROLE_STAGE_MAP.filter((r) => userPermissions.includes(r.permission)).flatMap((r) =>
      toStageList(r.stages)
    );

  const getViewerScopedStatus = (item) => {
    const viewerStages = getViewerStages();
    const approvalHistory = toArray(item?.approvalHistory);

    if (viewerStages.length > 0 && approvalHistory.length > 0) {
      const historyForViewer = approvalHistory
        .filter((h) => viewerStages.includes(h?.stage))
        .sort((a, b) => new Date(b?.timestamp || 0) - new Date(a?.timestamp || 0));

      const latestViewerDecision = historyForViewer[0];
      if (latestViewerDecision?.status) {
        const normalized = String(latestViewerDecision.status).trim().toLowerCase();
        if (normalized === 'approved') return 'approved';
        if (normalized === 'rejected') return 'rejected';
      }
    }

    return getNormalizedStatus(item) || 'pending';
  };

  const dedupeById = (items) => {
    const byId = new Map();
    items.forEach((item) => {
      const key = item?._id || item?.id;
      if (key) byId.set(key, item);
    });
    return Array.from(byId.values());
  };

  // Support both grouped API shape ({ pending, approved, rejected })
  // and flat API shape ({ items }) while enforcing viewer-scoped buckets.
  const pendingFromApi = toArray(data?.pending);
  const approvedFromApi = toArray(data?.approved);
  const rejectedFromApi = toArray(data?.rejected);
  const flatItems = toArray(data?.items || data?.patients);
  const allItems = dedupeById([
    ...pendingFromApi,
    ...approvedFromApi,
    ...rejectedFromApi,
    ...flatItems,
  ]);

  const pendingPatients = allItems.filter((item) => getViewerScopedStatus(item) === 'pending');
  const approvedPatients = allItems.filter((item) => getViewerScopedStatus(item) === 'approved');
  const rejectedPatients = allItems.filter((item) => getViewerScopedStatus(item) === 'rejected');

  const memoizedValue = useMemo(
    () => ({
      pendingPatients,
      approvedPatients,
      rejectedPatients,
      loading: isLoading,
      error,
      validating: isValidating,
      mutate,
    }),
    [pendingPatients, approvedPatients, rejectedPatients, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

// ----------------------------------------------------------------------

export function useGetPatient(patientId) {
  const URL = patientId ? endpoints.patients.details(patientId) : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR(URL, fetcher);

  const memoizedValue = useMemo(
    () => ({
      patient: data?.patient,
      claims: data?.claims || [],
      patientLoading: isLoading,
      patientError: error,
      patientValidating: isValidating,
      mutatePatient: mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

// ----------------------------------------------------------------------

export function useGetPatientLive(patientId, refreshInterval = 10_000) {
  const URL = patientId ? endpoints.patients.details(patientId) : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR(URL, fetcher, {
    refreshInterval,
    revalidateOnFocus: true,
  });

  const memoizedValue = useMemo(
    () => ({
      patient: data?.patient,
      claims: data?.claims || [],
      patientLoading: isLoading,
      patientError: error,
      patientValidating: isValidating,
      mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

// ----------------------------------------------------------------------
// Mutations

export async function createPatient(payload) {
  const res = await axios.post(endpoints.patients.create, payload);
  return res.data;
}

export async function approvePatient(patientId) {
  const res = await axios.post(endpoints.patients.approve(patientId));
  return res.data;
}

export async function rejectPatient(patientId, payload = {}) {
  const res = await axios.post(endpoints.patients.reject(patientId), payload);
  return res.data;
}

export async function getPatientDocument(patientId, docId, options = {}) {
  const res = await axios.get(`${endpoints.patients.documents(patientId)}/${docId}`, options);
  return res;
}

export async function faceLogin(faceId) {
  const res = await axios.post(endpoints.patients.faceLogin, { faceId });
  return res.data;
}

export async function generatePatientSessionToken(patientId) {
  const res = await axios.post(endpoints.patients.sessionToken(patientId));
  return res.data;
}

/** Save policy coverage table rows (non-zero invoice amounts only; server filters too). */
export async function savePolicyCoverageLines(patientId, payload) {
  const res = await axios.post(endpoints.patients.policyCoverage(patientId), payload);
  return res.data;
}

export async function createNewClaimForPatient(patientId, payload = {}) {
  const res = await axios.post(endpoints.claims.createFromPatient(patientId), payload);
  return res.data;
}

/** Persist the claim type (IPD/OPD/PA/etc) chosen during document collection. */
export async function saveClaimType(patientId, claimType) {
  const res = await axios.post(endpoints.patients.claimType(patientId), { claimType });
  return res.data;
}

export async function resetApprovalWorkflow(patientId) {
  const res = await axios.post(`/api/v1/patients/${patientId}/reset-approval`);
  return res.data;
}
